import React, { useMemo, useRef, useEffect, useLayoutEffect, useState, useCallback } from 'react';
import {
  VehicleViewProps,
  GapPosition,
  GapStatus,
} from './VehicleGapDetection.types';
import { SedanSVG } from './SedanSVG';
import { VanSVG } from './VanSVG';
import { HatchbackSVG } from './HatchbackSVG';
import { GapPointMarker } from './GapPointMarker';
import { getVehiclePointsMap } from './pointsMapping';
import { getVehicleSvgSize, getVehicleSvgStyle } from '../shared/vehicleSvgSizeMap';
import clsx from 'clsx';

/**
 * Komponent pojedynczego widoku pojazdu z punktami kontrolnymi
 *
 * Logika działania:
 * 1. Pobiera mapowanie punktów dla danego typu pojazdu
 * 2. Iteruje po wszystkich możliwych pozycjach (A-J)
 * 3. Pokazuje punkt TYLKO jeśli ma zdefiniowane współrzędne (nie jest undefined)
 * 4. Status i kolor punktu pobiera z API (props.points)
 * 5. Jeśli brak danych z API, używa domyślnego statusu 'OK'
 *
 * Struktura renderowania:
 * - SVG pojazdu jako tło
 * - Overlay div z position: absolute dla markerów
 */
export const VehicleView: React.FC<VehicleViewProps> = ({
  view,
  vehicleType,
  points,
  label,
  highlightedPoint,
  onPointHover,
  onPointClick,
}) => {
  const svgContainerRef = useRef<HTMLDivElement>(null);
  const [svgLoaded, setSvgLoaded] = useState(0);
  const handleSvgLoad = useCallback(() => setSvgLoaded((n) => n + 1), []);

  const VehicleSVG =
    vehicleType === 'sedan'
      ? SedanSVG
      : vehicleType === 'van'
        ? VanSVG
        : HatchbackSVG;

  // Mierzymy faktyczną szerokość kontenera vs designWidth z sizeMap. Gdy parent jest węższy,
  // car SVG shrinkuje uniformowo (preserveAspectRatio) — markery muszą się przybliżać do środka,
  // żeby zostać "na bryle".
  const designWidth = getVehicleSvgSize(vehicleType, view).maxWidth;
  const [autoScale, setAutoScale] = useState(1);

  useLayoutEffect(() => {
    const el = svgContainerRef.current;
    if (!el) return;

    const update = () => {
      const w = el.offsetWidth;
      if (w > 0) {
        setAutoScale(Math.min(1, w / designWidth));
      }
    };
    update();

    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, [designWidth]);

  // Pobierz mapowanie punktów dla tego typu pojazdu
  const pointsMap = useMemo(
    () => getVehiclePointsMap(vehicleType),
    [vehicleType],
  );

  // Utwórz mapę pozycja -> status z danych API dla szybkiego wyszukiwania
  const statusMap = useMemo(() => {
    const map = new Map<GapPosition, GapStatus>();
    points.forEach((point) => {
      map.set(point.position, point.status);
    });
    return map;
  }, [points]);

  // Filtruj punkty do wyświetlenia:
  // - muszą mieć zdefiniowane współrzędne (nie undefined)
  // - dla tego widoku (left/top/right)
  const pointsToDisplay = useMemo(() => {
    // Wszystkie możliwe pozycje punktów
    const allPositions: GapPosition[] = [
      'A',
      'B',
      'C',
      'D',
      'E',
      'F',
      'G',
      'H',
      'I',
      'J',
    ];

    return allPositions
      .map((position) => {
        const coords = pointsMap[view][position];

        // Pomijamy punkt jeśli nie ma współrzędnych
        if (!coords) return null;

        // Pobierz status z API lub użyj domyślnego
        const status = statusMap.get(position) || 'OK';

        return {
          position,
          coords,
          status,
        };
      })
      .filter((point) => point !== null);
  }, [pointsMap, view, statusMap]);

  // Podświetlenie ścieżek SVG na podstawie highlightedPoint
  useEffect(() => {
    const container = svgContainerRef.current;
    if (!container) return;

    // Usuń istniejące podświetlenia
    const allHighlighted = container.querySelectorAll('path.gap-highlight');
    allHighlighted.forEach((path) => {
      path.classList.remove('gap-highlight');
    });

    // Dodaj podświetlenie do pasujących ścieżek
    if (highlightedPoint) {
      // Obsługa space-separated values (np. "A B" dla vana)
      const matchingPaths = container.querySelectorAll(
        `path[data-gap-point~="${highlightedPoint}"]`
      );
      matchingPaths.forEach((path) => {
        path.classList.add('gap-highlight');
      });
    }
  }, [highlightedPoint, svgLoaded]);

  return (
    <div className="flex flex-col gap-3 self-stretch">
      {/* Etykieta widoku */}
      <div className={clsx('text-xs font-normal text-blue-300 pt-2', label?.length > 0 ? 'pt-0' : 'pt-4', {
        'text-left pl-10 lg:pl-0': view === 'left',
        'text-right pr-10 lg:pr-0': view === 'right',
      })}>{label}</div>

      {/* SVG pojazdu z punktami */}
      <div ref={svgContainerRef} className="vehicle-svg-container relative w-full mx-auto" style={getVehicleSvgStyle(vehicleType, view)}>
        {/* Sylwetka pojazdu */}
        <VehicleSVG view={view} className="w-full h-full" onLoad={handleSvgLoad} />

        {/* Overlay z markerami punktów kontrolnych (div zamiast SVG) */}
        <div className="absolute inset-0 w-full h-full pointer-events-none">
          {pointsToDisplay.map((point) => (
            <GapPointMarker
              key={point.position}
              position={point.position}
              status={point.status}
              x={point.coords.x}
              y={50 + autoScale * (point.coords.y - 50)}
              isHighlighted={highlightedPoint === point.position}
              onHover={onPointHover}
              onClick={onPointClick}
            />
          ))}
        </div>
      </div>
    </div>
  );
};
